import React from 'react';
import { useTranslation } from 'react-i18next';
import {ReactComponent as ThumbUpIcon} from './assets/thumbUp.svg';
import {ReactComponent as ThumbDownIcon} from './assets/thumbDown.svg';
import {ReactComponent as ThumbUpActiveIcon} from './assets/thumbUpFilled.svg';
import {ReactComponent as ThumbDownActiveIcon} from './assets/thumbDownFilled.svg';
import {ReactComponent as WarningIcon} from './assets/error.svg';
import { ReactComponent as TipIcon } from './assets/tip.svg';
import { Topic, Tip } from './neuralSearchAPI';
import styles from './QueryAssistantModal.module.scss';

interface FormattedToken {
  text: string;
  color?: string;
}

interface AIAnswerMessageProps {
  messageId: string;
  query?: string;
  content?: string;
  isError?: boolean;
  showOptimizeSyntax?: boolean;
  topics?: Topic[];
  tip?: Tip;
  showTopics?: boolean;
  onInsertQuery: (query: string) => void;
  onTopicClick: (topic: Topic) => void;
  onFeedback: (messageId: string, isHelpful: boolean) => void;
  formatQuery: (query: string) => FormattedToken[];
}

export const AIAnswerMessage: React.FC<AIAnswerMessageProps> = ({
  messageId,
  query,
  content,
  isError = false,
  showOptimizeSyntax = false,
  topics,
  tip,
  showTopics = false,
  onInsertQuery,
  onTopicClick,
  onFeedback,
  formatQuery,
}) => {
  const { t } = useTranslation();
  const [feedbackGiven, setFeedbackGiven] = React.useState<boolean | null>(null);
  const formattedTokens = query ? formatQuery(query) : [];

  const handleFeedback = (isHelpful: boolean) => {
    const newFeedback = feedbackGiven === isHelpful ? null : isHelpful;
    setFeedbackGiven(newFeedback);

    if (newFeedback !== null) {
      onFeedback(messageId, newFeedback);
    }
  };

  if (isError) {
    return (
      <div className={styles.messageAssistant}>
        <div className={`${styles.messageContent} ${styles.errorMessage}`}>
          <div className={styles.errorIcon}>
            {<WarningIcon />}
          </div>
          <div className={styles.errorText}>{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.messageAssistant}>
      <div className={styles.messageContent}>
        {showOptimizeSyntax ? (
          <div className={styles.optimizeSyntaxHint}>
            {t('Adjust this query by describing what you want to change.')}
          </div>
        ) : (
          <div className={styles.suggestedQueryHint}>
            {t("Here's your search query")}
          </div>
        )}
        {query && (
          <div className={styles.queryBlock}>
            <div className={styles.queryText}>
              {formattedTokens.map((token, index) => (
                <span
                  key={`${messageId}-token-${index}`}
                  style={{ color: token.color || 'inherit' }}
                >
                  {token.text}
                </span>
              ))}
            </div>
            {!showOptimizeSyntax ? <div className={styles.queryActions}>
              <button
                className={styles.insertButton}
                onClick={() => onInsertQuery(query)}
              >
                {t('Add to search')}
              </button>
              <div className={styles.feedbackButtons}>
                <span className={styles.feedbackLabel}>{t('Rate this query')}</span>
                <button
                  className={`${styles.feedbackButton} ${
                    feedbackGiven === true ? styles.feedbackButtonActive : ''
                  }`}
                  onClick={() => handleFeedback(true)}
                  title={t('Helpful')}
                >
                  {feedbackGiven === true ? (
                    <ThumbUpActiveIcon className={styles.feedbackIcon} />
                  ) : (
                    <ThumbUpIcon className={styles.feedbackIcon} />
                  )}
                </button>
                <button
                  className={`${styles.feedbackButton} ${
                    feedbackGiven === false ? styles.feedbackButtonActive : ''
                  }`}
                  onClick={() => handleFeedback(false)}
                  title={t('Not helpful')}
                >
                  {feedbackGiven === false ? (
                    <ThumbDownActiveIcon className={styles.feedbackIcon} />
                  ) : (
                    <ThumbDownIcon className={styles.feedbackIcon} />
                  )}
                </button>
              </div>
            </div> : ''}
          </div>
        )}
        {content && <div className={styles.assistantText}>{content}</div>}
        {tip && (() => {
          return (
            <>
              <div className={styles.tipMessage}>{tip?.message}</div>
              <div className={styles.tipsContainer}>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}>
                    <TipIcon />
                  </div>
                  <div className={styles.tipContent}>
                    <div className={styles.tipTitle}>{tip?.title}</div>
                    {tip?.text?.length > 0 && (
                      <ul className={styles.tipList}>
                        {tip?.text?.map((textItem, textIndex) => (
                          <li key={`tip-text-${textIndex}`}>{textItem}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </>
          );
        })()}
        {showTopics && topics && topics.length > 0 && (
          <>
            <div className={styles.tipMessage}>{t("You can add these related topics to your query:")}</div>
            <div className={styles.suggestionButtons}>
              {topics.map((topic, index) => (
                <button
                  key={`${topic.name}-${index}`}
                  className={styles.suggestionButton}
                  onClick={() => onTopicClick(topic)}
                >
                  {topic.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
